import { inverseTransaction, type JsonObject, type WorldDocument, type WorldEvent, type WorldTransaction } from "@private-fiction/story-core";
import { runtimeConfig } from "../config/runtimeConfig";
import { getSupabaseClient } from "./supabase";
import { isSyncBlocked } from "./syncFailure";
import { canRecoverLegacyMetadata, canRecoverSessionSchema, WORLD_WRITE_RPC, worldDeliveryHold, worldSyncTarget } from "./worldSyncPolicy";
import {
  acknowledgeStoryTransaction, claimStoryTransaction, commitLocalTransaction, getRuntimeCursor, listPendingStoryTransactions,
  loadStoryDocuments, loadStoryEvents, loadTransactionEvents, mergeRemoteStoryDocuments, mergeRemoteStoryEvents, noteStoryTransactionFailure, recoverSessionSchemaTransactions, seedStoryDocuments, setRuntimeCursor,
} from "./worldStore";

export type StorySyncPhase = "local" | "syncing" | "synced" | "offline" | "conflict" | "error";
export interface StorySyncState { phase: StorySyncPhase; pending: number; message?: string }

interface RemoteStoryDocument {
  story_local_id: string;
  document_id: string;
  document_kind: WorldDocument["kind"];
  schema_version: number;
  revision: number;
  title: string;
  summary: string;
  fields: JsonObject;
  runtime_state?: JsonObject;
  file_type_id?: string | null;
  references: WorldDocument["references"];
  context_visibility: WorldDocument["visibility"];
  display_visibility?: WorldDocument["displayVisibility"] | null;
  editable_by?: WorldDocument["editableBy"] | null;
  aliases?: string[] | null;
  tags?: string[] | null;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
}

interface RemoteStoryEvent {
  transaction_id: string;
  event_index: number;
  story_local_id: string;
  actor_type: WorldEvent["actor"];
  operation: WorldEvent["operation"];
  document_id: string;
  before_revision: number;
  after_revision: number;
  before_document: RemoteStoryDocument | null;
  after_document: RemoteStoryDocument;
  reason: string | null;
  source_message_ids: string[];
  created_at: string;
}

const mapRemoteDocument = (row: RemoteStoryDocument): WorldDocument => ({
  id: row.document_id, storyId: row.story_local_id, kind: row.document_kind,
  schemaVersion: row.schema_version, revision: Number(row.revision), title: row.title,
  summary: row.summary, fields: row.fields ?? {}, ...(row.runtime_state ? { runtimeState: row.runtime_state } : {}), ...(row.file_type_id ? { fileTypeId: row.file_type_id } : {}), references: row.references ?? [],
  visibility: row.context_visibility, createdAt: row.created_at, updatedAt: row.updated_at,
  ...(row.display_visibility ? { displayVisibility: row.display_visibility } : {}), ...(row.editable_by?.length ? { editableBy: row.editable_by } : {}), ...(row.aliases?.length ? { aliases: row.aliases } : {}), ...(row.tags?.length ? { tags: row.tags } : {}),
  ...(row.deleted_at ? { deletedAt: row.deleted_at } : {}),
});

const mapRemoteEvent = (row: RemoteStoryEvent): WorldEvent => ({
  transactionId: row.transaction_id,
  eventIndex: Number(row.event_index),
  storyId: row.story_local_id,
  actor: row.actor_type,
  operation: row.operation,
  documentId: row.document_id,
  beforeRevision: Number(row.before_revision),
  afterRevision: Number(row.after_revision),
  ...(row.before_document ? { before: mapRemoteDocument(row.before_document) } : {}),
  after: mapRemoteDocument(row.after_document),
  ...(row.reason ? { reason: row.reason } : {}),
  sourceMessageIds: Array.isArray(row.source_message_ids) ? row.source_message_ids : [],
  createdAt: row.created_at,
});

const remoteMutation = (mutation: WorldTransaction["mutations"][number]) => ({
  operation: mutation.operation,
  documentId: mutation.documentId,
  expectedRevision: mutation.expectedRevision,
  ...(mutation.document ? { document: {
    kind: mutation.document.kind, schemaVersion: mutation.document.schemaVersion,
    title: mutation.document.title, summary: mutation.document.summary, fields: mutation.document.fields, runtimeState: mutation.document.runtimeState ?? {}, fileTypeId: mutation.document.fileTypeId ?? null,
    references: mutation.document.references, visibility: mutation.document.visibility, displayVisibility: mutation.document.displayVisibility ?? null, editableBy: mutation.document.editableBy ?? [], aliases: mutation.document.aliases ?? [], tags: mutation.document.tags ?? [],
  } } : {}),
  reason: mutation.reason ?? "",
  sourceMessageIds: mutation.sourceMessageIds ?? [],
});

export class StoryRepository {
  #syncQueue: Promise<unknown> = Promise.resolve();
  #inflight = new Map<string, Promise<{ documents: WorldDocument[]; state: StorySyncState }>>();
  async hydrate(storyId: string, fallback: readonly WorldDocument[]): Promise<WorldDocument[]> {
    return seedStoryDocuments(storyId, fallback);
  }

  async initialize(storyId: string, documents: readonly WorldDocument[], userId?: string): Promise<WorldDocument[]> {
    const syncTarget = worldSyncTarget(runtimeConfig.supabaseUrl, userId);
    return seedStoryDocuments(storyId, documents, syncTarget ? { syncTarget } : {});
  }

  async commit(current: readonly WorldDocument[], transaction: WorldTransaction, userId?: string): Promise<WorldDocument[]> {
    return commitLocalTransaction(current, transaction, worldSyncTarget(runtimeConfig.supabaseUrl, userId));
  }

  async events(storyId: string): Promise<WorldEvent[]> { return loadStoryEvents(storyId); }

  async undo(current: readonly WorldDocument[], storyId: string, transactionId: string, at = new Date().toISOString(), userId?: string): Promise<WorldDocument[]> {
    const events = await loadTransactionEvents(storyId, transactionId);
    if (!events.length) throw new Error("ไม่พบ event ที่จะย้อนกลับ");
    return commitLocalTransaction(current, inverseTransaction(events, crypto.randomUUID(), at), worldSyncTarget(runtimeConfig.supabaseUrl, userId));
  }

  async state(): Promise<StorySyncState> {
    const pending = (await listPendingStoryTransactions()).length;
    return { phase: pending ? "local" : "synced", pending };
  }

  async sync(storyId: string): Promise<{ documents: WorldDocument[]; state: StorySyncState }> {
    return this.schedule(storyId, true);
  }

  /** Polling image status must never dispatch pending mutations. */
  async refresh(storyId: string): Promise<{ documents: WorldDocument[]; state: StorySyncState }> {
    return this.schedule(storyId, false);
  }

  private async schedule(storyId: string, sendPending: boolean): Promise<{ documents: WorldDocument[]; state: StorySyncState }> {
    const key = `${sendPending ? "sync" : "refresh"}:${storyId}`;
    const current = this.#inflight.get(key);
    if (current) return current;
    const pending = this.#syncQueue.catch(() => undefined).then(() => navigator.locks
      ? navigator.locks.request("mvp-story-world-sync", () => this.syncNow(storyId, sendPending))
      : this.syncNow(storyId, sendPending));
    this.#inflight.set(key, pending);
    this.#syncQueue = pending;
    try { return await pending; } finally { this.#inflight.delete(key); }
  }

  private async syncNow(storyId: string, sendPending: boolean): Promise<{ documents: WorldDocument[]; state: StorySyncState }> {
    let pendingBefore = await listPendingStoryTransactions(storyId);
    if (!runtimeConfig.structuredRuntimeEnabled) return {
      documents: await loadStoryDocuments(storyId),
      state: { phase: "local", pending: pendingBefore.length, message: "Structured world runtime ยังเก็บในเครื่องจนกว่า migration v1 จะถูก deploy" },
    };
    const supabase = getSupabaseClient();
    if (!supabase) return { documents: await loadStoryDocuments(storyId), state: { phase: "local", pending: pendingBefore.length } };
    const { data: auth } = await supabase.auth.getSession();
    if (!auth.session) return { documents: await loadStoryDocuments(storyId), state: { phase: "local", pending: pendingBefore.length, message: "เข้าสู่ระบบเพื่อซิงก์ข้ามอุปกรณ์" } };
    if (!navigator.onLine) return { documents: await loadStoryDocuments(storyId), state: { phase: "offline", pending: pendingBefore.length } };
    const target = worldSyncTarget(runtimeConfig.supabaseUrl, auth.session.user?.id);
    if (!target) return { documents: await loadStoryDocuments(storyId), state: { phase: "local", pending: pendingBefore.length, message: "ยังไม่ยืนยันบัญชีสำหรับการซิงก์" } };
    if (sendPending && pendingBefore.some(item => canRecoverSessionSchema(item, target) || canRecoverLegacyMetadata(item, target))) {
      const { data, error } = await supabase.rpc("initialize_session_file_types_v1", { p_session_id: storyId });
      if (!error && data?.sessionId === storyId && Array.isArray(data.fileTypeIds) && data.fileTypeIds.every((id: unknown) => typeof id === "string")) {
        await recoverSessionSchemaTransactions(storyId, target, data.fileTypeIds, data.fileTypeFieldKeys);
        pendingBefore = await listPendingStoryTransactions(storyId);
      }
    }
    const blocked = pendingBefore.find(isSyncBlocked);
    if (sendPending && blocked) return { documents: await loadStoryDocuments(storyId), state: {
      phase: ["40001", "PT409"].includes(blocked.lastErrorCode ?? "") || /revision conflict/i.test(blocked.lastError ?? "") ? "conflict" : "error",
      pending: pendingBefore.length, message: "หยุดส่งรายการที่ล้มซ้ำแล้ว เก็บข้อมูลในเครื่องไว้เพื่อตรวจความต่างกับ Cloud ก่อนส่งใหม่",
    } };
    if (sendPending && pendingBefore.some(item => (item.nextAttemptAt ?? 0) > Date.now())) return { documents: await loadStoryDocuments(storyId), state: { phase: "local", pending: pendingBefore.length, message: "รอก่อนลองเชื่อมต่อ Cloud อีกครั้ง" } };
    const hold = pendingBefore.map(item => worldDeliveryHold(item, target)).find(Boolean);
    if (sendPending && hold) return { documents: await loadStoryDocuments(storyId), state: { phase: "conflict", pending: pendingBefore.length, message: hold } };

    for (const pending of sendPending ? pendingBefore.slice(0, 20) : []) {
      if (!(await claimStoryTransaction(pending.id, target))) return { documents: await loadStoryDocuments(storyId), state: { phase: "local", pending: pendingBefore.length, message: "รายการกำลังส่งหรือรอตรวจสอบ จึงหยุดการส่งซ้ำ" } };
      // No fallback to v1: a restored database must install the safe endpoint first.
      const { data, error } = await supabase.rpc(WORLD_WRITE_RPC, {
        p_story_local_id: pending.transaction.storyId,
        p_transaction_id: pending.transaction.id,
        p_actor_type: pending.transaction.actor,
        p_mutations: pending.transaction.mutations.map(remoteMutation),
      }).then(response => response, (error: unknown) => ({ data: null, error: { code: "", message: error instanceof Error ? error.message : String(error) } }));
      if (error) {
        const missingEndpoint = ["PGRST202", "42883"].includes(error.code);
        await noteStoryTransactionFailure(pending, missingEndpoint ? { code: "PT409", message: "ฐานข้อมูลยังไม่พร้อมรับการเขียนรุ่นใหม่ เก็บรายการไว้ก่อน" } : error);
        const conflict = ["40001", "PT409"].includes(error.code) || /revision conflict/i.test(error.message);
        return { documents: await loadStoryDocuments(storyId), state: { phase: conflict ? "conflict" : "error", pending: pendingBefore.length, message: error.message } };
      }
      if (!data) {
        const error = new Error("ระบบคลาวด์ไม่สามารถบันทึกข้อมูลการเล่นได้ในขณะนี้");
        await noteStoryTransactionFailure(pending, error);
        return { documents: await loadStoryDocuments(storyId), state: { phase: "error", pending: pendingBefore.length, message: error.message } };
      }
      if (data.transactionId !== pending.transaction.id || data.storyLocalId !== pending.transaction.storyId || !Array.isArray(data.results)
        || data.results.length !== pending.transaction.mutations.length
        || pending.transaction.mutations.some(mutation => !data.results.some((result: { documentId?: string; revision?: number }) => result.documentId === mutation.documentId && result.revision === mutation.expectedRevision + 1))) {
        await noteStoryTransactionFailure(pending, { code: "PT409", message: "คำตอบยืนยันการบันทึกไม่ตรงกับรายการ เก็บข้อมูลไว้ตรวจสอบ" });
        return { documents: await loadStoryDocuments(storyId), state: { phase: "conflict", pending: pendingBefore.length, message: "ยังไม่ลบรายการในเครื่อง เพราะคำตอบยืนยันไม่ตรงกัน" } };
      }
      await acknowledgeStoryTransaction(pending.id);
    }

    const documentCursorKey = `story-runtime-v2:${target}`;
    const eventCursorKey = `story-events-v2:${target}`;
    const remaining = (await listPendingStoryTransactions(storyId)).length;
    const pageBudget = sendPending ? 20 : 1;
    let hasMore = false;
    let cursor = await getRuntimeCursor(documentCursorKey);
    let pageSize = 50;
    for (let page = 0; page < pageBudget; page += 1) {
      const { data, error } = await supabase.rpc("pull_story_runtime_v1", { p_after: cursor, p_limit: pageSize });
      if (error && /timeout|57014/i.test(`${error.code} ${error.message}`) && pageSize > 1) { pageSize = Math.max(1, Math.floor(pageSize / 2)); continue; }
      if (error) return { documents: await loadStoryDocuments(storyId), state: { phase: "error", pending: remaining, message: error.message } };
      const rows = (data?.documents ?? []) as RemoteStoryDocument[];
      await mergeRemoteStoryDocuments(rows.map(mapRemoteDocument));
      const nextCursor = Number(data?.cursor ?? cursor);
      if (rows.length && (!Number.isFinite(nextCursor) || nextCursor <= cursor)) return { documents: await loadStoryDocuments(storyId), state: { phase: "error", pending: remaining, message: "Cloud cursor ไม่เดินหน้า จึงหยุดอ่านซ้ำ" } };
      if (nextCursor > cursor) { cursor = nextCursor; await setRuntimeCursor(documentCursorKey, cursor); }
      if (rows.length < pageSize) break;
      if (page === pageBudget - 1) hasMore = true;
    }
    let eventCursor = await getRuntimeCursor(eventCursorKey);
    pageSize = 25;
    for (let page = 0; page < pageBudget; page += 1) {
      const { data, error } = await supabase.rpc("pull_story_events_v1", { p_after: eventCursor, p_limit: pageSize });
      if (error && /timeout|57014/i.test(`${error.code} ${error.message}`) && pageSize > 1) { pageSize = Math.max(1, Math.floor(pageSize / 2)); continue; }
      if (error) return { documents: await loadStoryDocuments(storyId), state: { phase: "error", pending: remaining, message: error.message } };
      const rows = (data?.events ?? []) as RemoteStoryEvent[];
      await mergeRemoteStoryEvents(rows.map(mapRemoteEvent));
      const nextCursor = Number(data?.cursor ?? eventCursor);
      if (rows.length && (!Number.isFinite(nextCursor) || nextCursor <= eventCursor)) return { documents: await loadStoryDocuments(storyId), state: { phase: "error", pending: remaining, message: "Cloud event cursor ไม่เดินหน้า จึงหยุดอ่านซ้ำ" } };
      if (nextCursor > eventCursor) { eventCursor = nextCursor; await setRuntimeCursor(eventCursorKey, eventCursor); }
      if (rows.length < pageSize) break;
      if (page === pageBudget - 1) hasMore = true;
    }
    if (runtimeConfig.compactTurnsEnabled) {
      // One database snapshot combines materialized files and confirmed pending
      // outcomes, including the race where a worker finishes during this pull.
      const {data,error}=await supabase.rpc('get_effective_story_runtime_v1',{p_story_local_id:storyId});
      if(error || !Array.isArray(data?.documents)) return {documents:await loadStoryDocuments(storyId),state:{phase:'error',pending:remaining,message:error?.message || 'ยังอ่านผลเทิร์นที่ยืนยันแล้วไม่ได้'}};
      if(data.documents.some((document:WorldDocument)=>document.storyId!==storyId)) return {documents:await loadStoryDocuments(storyId),state:{phase:'error',pending:remaining,message:'ผลเทิร์นอ้างอิงคนละเซสชัน'}};
      await mergeRemoteStoryDocuments(data.documents as WorldDocument[]);
    }
    const pendingAfter = (await listPendingStoryTransactions(storyId)).length;
    return { documents: await loadStoryDocuments(storyId), state: { phase: hold ? "conflict" : pendingAfter || hasMore ? "local" : "synced", pending: pendingAfter, ...(hold ? { message: hold } : {}) } };
  }
}
